/**
 * Response loop workbench strip — Before / During / After (Incident OS §7).
 * Soft operator language only. Actionable shortcuts (not decorative chrome).
 * Auto Sentinel policy stays under Settings `#/auto-sentinel`.
 * Master: 2026-07-26-incident-os-ai-srf-unified-master.md §2 / §7
 */

import { escapeHtml } from "./dom.js";
import { softOperatorNote } from "./format.js";

/**
 * Soft-map AI incident list → MAIC §5.5 card fields (Home · AI Context).
 * @param {unknown} items
 * @returns {{ waitingN: number, category?: string, riskScore?: number|null, reasonLine?: string, responderHint?: string, leadId?: string, modelUsed?: string, modelTier?: string }}
 */
export function maicFieldsFromAiItems(items) {
  const list = Array.isArray(items) ? items : [];
  const waiting = list.filter((it) => {
    const st = String((it && it.status) || "").toLowerCase();
    return !st || st === "pending" || st === "open";
  });
  const waitingN = waiting.length;
  const first = waiting[0] || list[0] || null;
  if (!first) return { waitingN: 0 };
  const category = softOperatorNote(String(
    first.category || first.classification || first.alarm_class || "",
  )) || "";
  const riskRaw = first.risk_score != null ? first.risk_score : first.risk;
  const riskScore = riskRaw != null && Number.isFinite(Number(riskRaw))
    ? Number(riskRaw)
    : null;
  const reasons = Array.isArray(first.reasons) ? first.reasons : [];
  const reasonRaw =
    first.summary
    || first.detail
    || first.recommendation
    || first.reason
    || first.why
    || first.model_reason
    || first.title
    || (reasons.length
      ? reasons.map((r) => String(r || "").trim()).filter(Boolean).slice(0, 2).join(" · ")
      : "");
  const reasonLine = softOperatorNote(String(reasonRaw || "")) || "";
  const leadId = String(first.incident_id || first.id || first.item_id || "").trim();
  const modelUsed = String(first.model_used || first.model_tier || first.model || "").trim();
  const modelTier = String(first.model_tier || first.honesty_tier || "").trim();
  return {
    waitingN,
    category,
    riskScore,
    reasonLine,
    responderHint: "Assign on Home · Find nearest · Fresh-only Soft — shared GPS · not fabric reach",
    leadId: waitingN > 0 ? leadId : "",
    modelUsed,
    modelTier,
  };
}

/**
 * Full Incident OS response chain (master §2.1) — each step is a usable desk.
 * @returns {{ id: string, label: string, hint: string, href: string, action: string }[]}
 */
export function incidentOsWorkflowSteps() {
  return [
    {
      id: "notify",
      label: "1 · Notify",
      hint: "Alerts already fired",
      href: "#/telegram",
      action: "Channels",
    },
    {
      id: "observe",
      label: "2 · Observe",
      hint: "Map · scene",
      href: "#/overview",
      action: "Home map",
    },
    {
      id: "triage",
      label: "3 · AI triage",
      hint: "Rules + model reason",
      href: "#/overview",
      action: "Show on map",
    },
    {
      id: "decide",
      label: "4 · Intervene",
      hint: "Hold · Takeover · Needs you",
      href: "#/overview",
      action: "On the loop",
    },
    {
      id: "assign",
      label: "5 · Assign",
      hint: "People · queue",
      href: "#/response",
      action: "Field queue",
    },
    {
      id: "field",
      label: "6 · Field",
      hint: "Accept → arrive",
      href: "#/response",
      action: "Status",
    },
    {
      id: "evidence",
      label: "7 · Evidence",
      hint: "Photos · scene text",
      href: "#/snapshots",
      action: "Photos",
    },
    {
      id: "proof",
      label: "8 · Proof",
      hint: "Close · export",
      href: "#/incidents",
      action: "Cases",
    },
  ];
}

/**
 * MAIC action desk (master §5.5) — usable cards, not JSON.
 * When waitingN / category / score present, show live numbers; else honest empty.
 * @param {{
 *   waitingN?: number,
 *   category?: string,
 *   riskScore?: number|null,
 *   reasonLine?: string,
 *   responderHint?: string,
 *   escalateSec?: number,
 * }} [opts]
 * @returns {string}
 */
export function maicActionWorkbenchHtml(opts = {}) {
  const waitingN = Number(opts.waitingN) || 0;
  const category = String(opts.category || "").trim();
  const risk = opts.riskScore != null && Number.isFinite(Number(opts.riskScore))
    ? Math.round(Number(opts.riskScore))
    : null;
  const reason = String(opts.reasonLine || "").trim();
  const people = String(opts.responderHint || "").trim();
  const escalateSec = Number(opts.escalateSec) > 0 ? Number(opts.escalateSec) : 15;

  const aiTitle = category
    ? category
    : (waitingN > 0 ? `${waitingN} waiting review` : "Classify · score · why");
  const aiScore = risk != null ? `Priority ${risk}` : "Rules own the score";
  const aiBody = reason
    ? reason
    : (waitingN > 0
      ? "Team needs you — Hold freezes auto writes; Takeover returns the wheel."
      : "Rules set priority · model may write a short why · Team runs unless Needs you");
  const peopleBody = people
    ? people
    : "Prefer fresh people nearby · Accept → arrive on Response";
  const waitLine =
    waitingN > 0
      ? `${waitingN} plan${waitingN === 1 ? "" : "s"} need interrupt · ~${escalateSec}s escalate if no one acts`
      : `Team running · ~${escalateSec}s escalate path only when Needs you`;

  return `
  <div class="croc-maic" role="list" aria-label="AI Context actions">
    <article class="croc-maic__card glass-panel glass-panel--inset" role="listitem">
      <p class="croc-maic__k">Tonight's plan</p>
      <strong class="croc-maic__v">${escapeHtml(aiTitle)}</strong>
      <p class="muted croc-maic__s">${escapeHtml(aiScore)}</p>
      <p class="croc-maic__body muted">${escapeHtml(aiBody)}</p>
      <div class="croc-maic__acts">
        <button type="button" class="btn sm btn-tap" data-cc-go="overview">Show on map</button>
        <button type="button" class="btn sm secondary btn-tap" data-cc-go="auto-sentinel">Policy</button>
      </div>
    </article>
    <article class="croc-maic__card glass-panel glass-panel--inset" role="listitem">
      <p class="croc-maic__k">Responder</p>
      <strong class="croc-maic__v">Who can go</strong>
      <p class="muted croc-maic__s">Map straight-line Soft · not fabric reach yet</p>
      <p class="croc-maic__body muted">${escapeHtml(peopleBody)}</p>
      <div class="croc-maic__acts">
        <button type="button" class="btn sm btn-tap" data-cc-go="response">Open queue</button>
        <button type="button" class="btn sm secondary btn-tap" data-cc-go="overview">Assign on Home</button>
      </div>
    </article>
    <article class="croc-maic__card glass-panel glass-panel--inset" role="listitem">
      <p class="croc-maic__k">Escalation</p>
      <strong class="croc-maic__v">Hold · Takeover</strong>
      <p class="muted croc-maic__s">${escapeHtml(waitLine)}</p>
      <div class="croc-maic__acts">
        <button type="button" class="btn sm btn-tap" data-cc-go="overview">Hold · Takeover</button>
        <button type="button" class="btn sm ghost btn-tap" data-cc-go="readiness">Before</button>
      </div>
    </article>
  </div>`;
}

/**
 * Usable Incident OS workflow workbench — not a watch-only plan card.
 * @param {{
 *   activeId?: string,
 *   title?: string,
 *   lede?: string,
 *   compact?: boolean,
 *   mapRail?: boolean,
 *   waitingN?: number,
 *   showMaic?: boolean,
 *   category?: string,
 *   riskScore?: number|null,
 *   reasonLine?: string,
 *   responderHint?: string,
 * }} [opts]
 */
export function incidentOsWorkflowWorkbenchHtml(opts = {}) {
  const title = String(opts.title || "Tonight's loop").trim() || "Tonight's loop";
  const waitingN = Number(opts.waitingN) || 0;
  const lede = String(
    opts.lede ||
      (waitingN > 0
        ? `${waitingN} plan${waitingN === 1 ? "" : "s"} waiting — open Mission Room, then Assign here.`
        : "Notify stays first · model may add a short why · Mission Room control · field proof closes the loop."),
  ).trim();
  const active = String(opts.activeId || "assign").trim().toLowerCase();
  const compact = !!opts.compact;
  const mapRail = !!opts.mapRail;
  // Default ON for explicit Incident OS desks; cascade helper may pass showMaic:false.
  const showMaic = opts.showMaic !== false;
  // Home map-first: collapse to a thin rail unless a plan is waiting.
  const expanded = mapRail ? waitingN > 0 : true;
  const steps = incidentOsWorkflowSteps();
  const pills = steps
    .map((s) => {
      const on =
        s.id === active ||
        (active === "people" && (s.id === "assign" || s.id === "field")) ||
        (active === "plan" && (s.id === "triage" || s.id === "decide")) ||
        (active === "notice" && (s.id === "notify" || s.id === "observe")) ||
        (active === "ready" && s.id === "notify") ||
        (active === "proof" && (s.id === "evidence" || s.id === "proof"));
      return `<button type="button" class="croc-ios-wf__step${on ? " is-active" : ""}" data-cc-go="${escapeHtml(String(s.href || "").replace(/^#\/?/, ""))}"${on ? ' aria-current="step"' : ""}>
        <span class="croc-ios-wf__n">${escapeHtml(s.label)}</span>
        <span class="muted croc-ios-wf__h">${escapeHtml(s.hint)}</span>
        <span class="croc-ios-wf__a">${escapeHtml(s.action)}</span>
      </button>`;
    })
    .join("");
  const railBar = mapRail
    ? `<div class="croc-ios-wf__rail-bar">
        <p class="croc-ios-wf__rail-label">Tonight's loop</p>
        <button type="button" class="btn sm ghost btn-tap croc-ios-wf__rail-toggle" data-ios-expand="1" aria-expanded="${expanded ? "true" : "false"}">${expanded ? "Hide details" : "Loop details"}</button>
        <button type="button" class="btn sm secondary btn-tap" data-cc-go="response">Queue</button>
      </div>`
    : "";
  return `
  <section class="croc-ios-wf glass-panel glass-panel--panel${compact ? " croc-ios-wf--compact" : ""}${mapRail ? " croc-ios-wf--map-rail" : ""}" data-ios-wf="1" data-ios-expanded="${expanded ? "1" : "0"}" aria-label="Tonight's loop">
    ${railBar}
    <div class="croc-ios-wf__head">
      <div>
        <p class="croc-ios-wf__eyebrow">Tonight's loop</p>
        <h3 class="croc-ios-wf__title">${escapeHtml(title)}</h3>
        <p class="muted croc-ios-wf__lede">${escapeHtml(lede)}</p>
      </div>
      <div class="croc-ios-wf__cta">
        <button type="button" class="btn sm btn-tap" data-cc-go="response/cases">Mission Room</button>
        <button type="button" class="btn sm secondary btn-tap" data-cc-go="response">Open Response</button>
        <button type="button" class="btn sm ghost btn-tap" data-cc-go="readiness">Before</button>
      </div>
    </div>
    <nav class="croc-ios-wf__steps" aria-label="Notify through proof">${pills}</nav>
    ${showMaic
      ? maicActionWorkbenchHtml({
          waitingN,
          category: opts.category,
          riskScore: opts.riskScore,
          reasonLine: opts.reasonLine,
          responderHint: opts.responderHint,
        })
      : ""}
    <p class="honest croc-ios-wf__note">Notify-first · the Watch owns the score · you Continue or Hold on the map · model may write a short why · frame scene text Soft · box detection and fabric reach not live · App GPS live on Home map.</p>
  </section>`;
}

/**
 * @returns {{ id: LoopStepId, label: string, href: string, hint: string }[]}
 */
export function responseLoopSteps() {
  return [
    {
      id: "ready",
      label: "Before",
      href: "#/readiness",
      hint: "Plans · drill · gaps",
    },
    {
      id: "notice",
      label: "During",
      href: "#/overview",
      hint: "Home · assign · AI",
    },
    {
      id: "plan",
      label: "AI gate",
      href: "#/overview",
      hint: "Show on map",
    },
    {
      id: "people",
      label: "Field",
      href: "#/response",
      hint: "Queue · siren",
    },
    {
      id: "proof",
      label: "After",
      href: "#/incidents",
      hint: "Proof · close",
    },
  ];
}

/**
 * Map legacy activeId aliases onto the five-step strip.
 * @param {string} raw
 * @returns {LoopStepId}
 */
function normalizeActiveId(raw) {
  const id = String(raw || "").trim().toLowerCase();
  if (id === "ready" || id === "readiness" || id === "before") return "ready";
  if (id === "notice" || id === "alert" || id === "during" || id === "home" || id === "map") return "notice";
  if (id === "plan" || id === "ai" || id === "auto" || id === "intel") return "plan";
  if (id === "people" || id === "dispatch" || id === "field" || id === "team") return "people";
  if (id === "proof" || id === "after" || id === "audit" || id === "close") return "proof";
  return "notice";
}

/**
 * Map five-step strip ids onto the full Incident OS 8-step chain.
 * @param {string} raw
 * @returns {string}
 */
function mapLoopToIosActiveId(raw) {
  const five = normalizeActiveId(raw);
  if (five === "ready") return "notify";
  if (five === "notice") return "observe";
  if (five === "plan") return "decide";
  if (five === "people") return "assign";
  if (five === "proof") return "proof";
  return "observe";
}

/**
 * Usable workflow strip for desks that are not Home.
 * Tip 499: defaults to full Incident OS 8-step + MAIC (master §2 / §5.5)
 * so every page that still calls this helper gets a real workbench.
 *
 * @param {{
 *   compact?: boolean,
 *   activeId?: string,
 *   title?: string,
 *   lede?: string,
 *   extraActionsHtml?: string,
 *   legacyFiveStep?: boolean,
 *   showMaic?: boolean,
 *   waitingN?: number,
 * }} [opts]
 * @returns {string}
 */
export function responseLoopWorkbenchHtml(opts = {}) {
  const title = String(opts.title || "Response workbench").trim() || "Response workbench";
  const lede = String(opts.lede || "Stay on the living loop — Home for assign and field steps, Settings only for how Sentinel thinks.").trim();
  const compact = !!opts.compact;
  const extra = String(opts.extraActionsHtml || "").trim();

  // Default: full usable Incident OS desk (not a watch-only 5-pill strip).
  // Tip 500: MAIC only when caller asks or there are waiting plans — avoid fake AI on Platform/OTA.
  if (!opts.legacyFiveStep) {
    const waitingN = Number(opts.waitingN) || 0;
    const showMaic = opts.showMaic === true || waitingN > 0;
    const ios = incidentOsWorkflowWorkbenchHtml({
      compact,
      activeId: mapLoopToIosActiveId(opts.activeId),
      title,
      lede,
      showMaic,
      waitingN,
      category: opts.category,
      riskScore: opts.riskScore,
      reasonLine: opts.reasonLine,
      responderHint: opts.responderHint,
    });
    if (!extra) return ios;
    return `${ios}
  <div class="croc-loop-wb__extra" role="group" aria-label="Desk actions">${extra}</div>`;
  }

  const active = normalizeActiveId(opts.activeId);
  const steps = responseLoopSteps();
  const pills = steps
    .map((s) => {
      const on = s.id === active;
      return `<button type="button" class="croc-loop-wb__step${on ? " is-active" : ""}" data-cc-go="${escapeHtml(String(s.href || "").replace(/^#\/?/, ""))}"${on ? ' aria-current="step"' : ""}>
        <span class="croc-loop-wb__step-label">${escapeHtml(s.label)}</span>
        <span class="muted croc-loop-wb__step-hint">${escapeHtml(s.hint)}</span>
      </button>`;
    })
    .join("");
  return `
  <section class="croc-loop-wb glass-panel glass-panel--panel${compact ? " croc-loop-wb--compact" : ""}" data-loop-wb="1" aria-label="Response workbench">
    <div class="croc-loop-wb__head">
      <div>
        <p class="croc-loop-wb__eyebrow">Workflow</p>
        <h3 class="croc-loop-wb__title">${escapeHtml(title)}</h3>
        <p class="muted croc-loop-wb__lede">${escapeHtml(lede)}</p>
      </div>
      <div class="croc-loop-wb__actions">
        <button type="button" class="btn sm btn-tap" data-cc-go="overview">Home workbench</button>
        <button type="button" class="btn sm secondary btn-tap" data-cc-go="auto-sentinel">Policy</button>
        ${extra}
      </div>
    </div>
    <nav class="croc-loop-wb__steps" aria-label="Before, during, after">${pills}</nav>
  </section>`;
}

/**
 * Compact AI / loop innovation cards for desks (soft copy only — no backend jargon).
 * @param {"before"|"during"|"field"|"after"|"notify"} [kind]
 * @returns {string}
 */
export function responseAiInnovHtml(kind = "during") {
  const packs = {
    before: [
      { k: "Notify-first", v: "Alerts keep going", s: "AI never blocks the first call" },
      { k: "Plans", v: "Certified before live", s: "Drill closes real gaps" },
      { k: "Readiness", v: "Score the team", s: "Fix top gaps before night shift" },
      { k: "Vision", v: "Frame scene text", s: "Honest Soft · box detection not live" },
    ],
    during: [
      { k: "Notify-first", v: "Alerts keep going", s: "AI never blocks the first call" },
      { k: "Rules score", v: "Auditable priority", s: "Never silently lowers urgency" },
      { k: "Team autonomy", v: "Hold · Takeover", s: "On-the-loop · Team runs; interrupt only on exception" },
      { k: "Field", v: "Accept · travel · arrive", s: "Queue stays on Response" },
    ],
    ai: [
      { k: "Notify-first", v: "Alerts keep going", s: "AI never blocks the first call" },
      { k: "Classify", v: "Emergency · Security · Fault", s: "Plain labels the team can act on" },
      { k: "Rules score", v: "0–100 priority", s: "Auditable · models never set the score" },
      { k: "Written reasons", v: "Reason · suggest", s: "Short words only · not the score" },
      { k: "Advisory analysis", v: "Category · priority · why", s: "Team runs unless Needs you" },
      { k: "Responder", v: "Who can go", s: "Map straight-line Soft · not fabric reach" },
      { k: "Escalation", v: "Hold · Takeover", s: "Team running · ~15s escalate path only when Needs you" },
      { k: "On the loop", v: "Map · rail Hold / Takeover", s: "Hold · Takeover — not a second desk gate" },
      { k: "Recommend", v: "Who is nearest", s: "Fresh people first · stale stays labeled" },
      { k: "Vision", v: "Frame ask · scene text", s: "Preview · boxes not live" },
      { k: "Proof", v: "Who acted · when", s: "Timeline keeps who acted and when" },
    ],
    notify: [
      { k: "Notify-first", v: "Chat & voice fire now", s: "AI never waits on this desk" },
      { k: "Connect once", v: "One-time link code", s: "Then alerts keep flowing" },
      { k: "On the loop", v: "Home map · rail", s: "Hold · Takeover — not a second desk gate" },
      { k: "Home", v: "Assign after alert", s: "Map and queue stay on Home" },
    ],
    field: [
      { k: "Notify-first", v: "You already got the call", s: "Assign does not wait on AI" },
      { k: "On the loop", v: "Interrupt anytime", s: "Hold freezes Team; Takeover returns the wheel" },
      { k: "Nearest", v: "Fresh people first", s: "Stale GPS stays labeled" },
      { k: "Proof", v: "Close with evidence", s: "Open the case when done" },
    ],
    after: [
      { k: "Proof", v: "Who acted · when", s: "Export when you need a record" },
      { k: "Timeline", v: "One living case", s: "Notes stay with the alarm" },
      { k: "Photos", v: "Scene when ready", s: "Missing photos never block close" },
      { k: "Notify-first", v: "Still true after", s: "Next alarm still fires instantly" },
    ],
  };
  const cards = packs[kind] || packs.during;
  const dense = kind === "ai" ? " croc-as-innov--dense" : "";
  return `<div class="croc-as-innov croc-as-innov--desk${dense}" role="list" aria-label="How Sentinel thinks here">
    ${cards.map((c) => `
      <article class="croc-as-innov__card glass-panel glass-panel--inset" role="listitem">
        <p class="croc-as-innov__k">${escapeHtml(c.k)}</p>
        <strong class="croc-as-innov__v">${escapeHtml(c.v)}</strong>
        <p class="muted croc-as-innov__s">${escapeHtml(c.s)}</p>
      </article>`).join("")}
  </div>`;
}
